import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles, Loader2, ImagePlus, X, FileText, ArrowLeft, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { trainAgent, trainScenario, ConversationMessage } from '../../lib/aiAgents';
import { useImageViewer } from '../ImageViewerProvider';
import toast from 'react-hot-toast';

interface AgentChatPanelProps {
  agentId: string;
  agentName: string;
  agentPurpose: string | null;
  businessId: string;
  conversationId: string | null;
  messages: ConversationMessage[];
  onConversationUpdate: (conversationId: string, messages: ConversationMessage[]) => void;
  onRulesChanged: () => void;
  mode: 'training' | 'scenario';
  scenarioId: string | null;
  scenarioMessages: ConversationMessage[];
  onStartScenario: () => void;
  onExitScenario: () => void;
  onScenarioUpdate: (scenarioId: string, messages: ConversationMessage[]) => void;
  onScenarioSaved: () => void;
}

export default function AgentChatPanel({
  agentId,
  agentName,
  agentPurpose,
  businessId,
  conversationId,
  messages,
  onConversationUpdate,
  onRulesChanged,
  mode,
  scenarioId,
  scenarioMessages,
  onStartScenario,
  onExitScenario,
  onScenarioUpdate,
  onScenarioSaved,
}: AgentChatPanelProps) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingImages, setPendingImages] = useState<{ file: File; preview: string }[]>([]);
  const { open: openImageViewer } = useImageViewer();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isScenarioMode = mode === 'scenario';
  const activeMessages = isScenarioMode ? scenarioMessages : messages;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeMessages]);

  useEffect(() => {
    return () => {
      pendingImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, []);

  const uploadImage = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const subFolder = isScenarioMode ? 'agent-scenarios' : 'agent-training';
    const filePath = `${subFolder}/${agentId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('user-uploads')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from('user-uploads')
      .getPublicUrl(filePath);

    return urlData?.publicUrl || null;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: { file: File; preview: string }[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      newImages.push({ file, preview: URL.createObjectURL(file) });
    }

    setPendingImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingImage = (index: number) => {
    setPendingImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if ((!trimmed && pendingImages.length === 0) || isLoading) return;

    setIsLoading(true);

    let imageUrls: string[] = [];
    if (pendingImages.length > 0) {
      const uploads = await Promise.all(pendingImages.map(img => uploadImage(img.file)));
      imageUrls = uploads.filter((url): url is string => url !== null);
      if (imageUrls.length < pendingImages.length) {
        toast.error('Some images failed to upload');
      }
      pendingImages.forEach(img => URL.revokeObjectURL(img.preview));
      setPendingImages([]);
    }

    const userMsg: ConversationMessage = {
      role: 'user',
      content: trimmed || (imageUrls.length > 0 ? `[Shared ${imageUrls.length} photo${imageUrls.length > 1 ? 's' : ''}]` : ''),
      timestamp: new Date().toISOString(),
      rules_extracted: [],
      images: imageUrls.length > 0 ? imageUrls : undefined
    };

    const updatedMessages = [...activeMessages, userMsg];

    if (isScenarioMode) {
      onScenarioUpdate(scenarioId || '', updatedMessages);
    } else {
      onConversationUpdate(conversationId || '', updatedMessages);
    }
    setInput('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      if (isScenarioMode) {
        const result = await trainScenario(
          agentId,
          trimmed,
          businessId,
          scenarioId || undefined,
          imageUrls.length > 0 ? imageUrls : undefined,
          'chat'
        );

        if (!result.success) {
          const errorMsg: ConversationMessage = {
            role: 'assistant',
            content: result.insufficientTokens
              ? "You've run out of AI tokens. Please purchase more tokens to continue."
              : `Something went wrong: ${result.error || 'Unknown error'}`,
            timestamp: new Date().toISOString(),
            rules_extracted: []
          };
          onScenarioUpdate(result.scenario_id || scenarioId || '', [...updatedMessages, errorMsg]);
          return;
        }

        const rulesExtracted = [
          ...(result.rules_created || []).map(r => r.id),
          ...(result.rules_updated || []).map(r => r.id)
        ];

        const assistantMsg: ConversationMessage = {
          role: 'assistant',
          content: result.response || '',
          timestamp: new Date().toISOString(),
          rules_extracted: rulesExtracted
        };

        onScenarioUpdate(result.scenario_id || scenarioId || '', [...updatedMessages, assistantMsg]);

        if (rulesExtracted.length > 0 || (result.rules_deleted || []).length > 0) {
          onRulesChanged();
        }
      } else {
        const result = await trainAgent(agentId, trimmed, businessId, conversationId || undefined, imageUrls.length > 0 ? imageUrls : undefined);

        if (!result.success) {
          const errorMsg: ConversationMessage = {
            role: 'assistant',
            content: result.insufficientTokens
              ? "You've run out of AI tokens. Please purchase more tokens to continue training."
              : `Something went wrong: ${result.error || 'Unknown error'}`,
            timestamp: new Date().toISOString(),
            rules_extracted: []
          };
          onConversationUpdate(result.conversation_id || conversationId || '', [...updatedMessages, errorMsg]);
          return;
        }

        const rulesExtracted = [
          ...(result.rules_created || []).map(r => r.id),
          ...(result.rules_updated || []).map(r => r.id)
        ];

        const assistantMsg: ConversationMessage = {
          role: 'assistant',
          content: result.response || '',
          timestamp: new Date().toISOString(),
          rules_extracted: rulesExtracted
        };

        onConversationUpdate(result.conversation_id || conversationId || '', [...updatedMessages, assistantMsg]);

        if (rulesExtracted.length > 0 || (result.rules_deleted || []).length > 0) {
          onRulesChanged();
        }
      }
    } catch (err) {
      console.error('Error:', err);
      const errorMsg: ConversationMessage = {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
        rules_extracted: []
      };
      if (isScenarioMode) {
        onScenarioUpdate(scenarioId || '', [...updatedMessages, errorMsg]);
      } else {
        onConversationUpdate(conversationId || '', [...updatedMessages, errorMsg]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveScenario = async () => {
    if (scenarioMessages.length === 0) {
      toast.error('Add some messages to the scenario before saving');
      return;
    }
    setIsSaving(true);
    try {
      const result = await trainScenario(
        agentId,
        'Please save and summarize this scenario.',
        businessId,
        scenarioId || undefined,
        undefined,
        'save'
      );

      if (!result.success) {
        toast.error(result.error || 'Failed to save scenario');
        return;
      }

      toast.success('Scenario saved!');
      onScenarioSaved();
      onRulesChanged();
    } catch (err) {
      console.error('Save scenario error:', err);
      toast.error('Failed to save scenario');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const welcomeMessage = isScenarioMode
    ? `Let's walk through a real job scenario! Share a photo and tell me about this job -- what you saw, what you quoted, and why. I'll help break down the estimate step by step.`
    : `Hi! I'm ready to learn about your business${agentPurpose ? ` and how to ${agentPurpose.toLowerCase()}` : ''}. Just tell me about your pricing, rates, rules, and how things work -- I'll organize everything automatically.\n\nYou can also share photos to show me real examples. What should I know first?`;

  const allMessages: ConversationMessage[] = activeMessages.length === 0
    ? [{ role: 'assistant', content: welcomeMessage, timestamp: new Date().toISOString(), rules_extracted: [] }]
    : activeMessages;

  return (
    <div className="flex flex-col h-full">
      {/* Scenario Mode Banner */}
      {isScenarioMode && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Scenario Mode</span>
            <span className="text-xs text-amber-600">Walk through a real job example</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveScenario}
              disabled={isSaving || isLoading || scenarioMessages.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save Scenario
            </button>
            <button
              onClick={onExitScenario}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-amber-700 hover:bg-amber-100 text-xs font-medium rounded-lg transition-colors"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to Training
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {allMessages.map((msg, idx) => (
          <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isScenarioMode ? 'bg-amber-100' : 'bg-blue-100'}`}>
                <Bot className={`h-4 w-4 ${isScenarioMode ? 'text-amber-600' : 'text-blue-600'}`} />
              </div>
            )}
            <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
              {msg.images && msg.images.length > 0 && (
                <div className={`flex flex-wrap gap-2 mb-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.images.map((url, imgIdx) => (
                    <img
                      key={imgIdx}
                      src={url}
                      alt={`Photo ${imgIdx + 1}`}
                      className="w-48 h-36 object-cover rounded-xl border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageViewer({ images: (msg.images || []).map((u) => ({ src: u })), index: imgIdx })}
                    />
                  ))}
                </div>
              )}
              <div
                className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? isScenarioMode
                      ? 'bg-amber-600 text-white rounded-br-md'
                      : 'bg-blue-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
              >
                {msg.content}
              </div>
              {msg.role === 'assistant' && msg.rules_extracted && msg.rules_extracted.length > 0 && (
                <div className="flex items-center gap-1.5 mt-1.5 ml-1">
                  <Sparkles className="h-3 w-3 text-amber-500" />
                  <span className="text-xs text-amber-600 font-medium">
                    {msg.rules_extracted.length} rule{msg.rules_extracted.length > 1 ? 's' : ''} learned
                  </span>
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isScenarioMode ? 'bg-amber-600' : 'bg-blue-600'}`}>
                <User className="h-4 w-4 text-white" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 justify-start">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isScenarioMode ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Bot className={`h-4 w-4 ${isScenarioMode ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
              <span className="text-sm text-gray-400">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Pending Image Previews */}
      {pendingImages.length > 0 && (
        <div className="px-4 pt-3 border-t border-gray-100 bg-white">
          <div className="flex flex-wrap gap-2">
            {pendingImages.map((img, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={img.preview}
                  alt={`Pending ${idx + 1}`}
                  className="w-20 h-16 object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => removePendingImage(idx)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 p-4 bg-white">
        {!isScenarioMode && (
          <div className="mb-3">
            <button
              onClick={onStartScenario}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <FileText className="h-3.5 w-3.5" />
              Add Scenario
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isSaving}
            className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
            title="Add photos"
          >
            <ImagePlus className="h-5 w-5" />
          </button>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); handleTextareaInput(); }}
            onKeyDown={handleKeyDown}
            placeholder={
              isScenarioMode
                ? scenarioMessages.length === 0
                  ? "Describe the job and share photos..."
                  : "Continue describing the scenario..."
                : messages.length === 0
                  ? "Tell me about your business..."
                  : "Continue training..."
            }
            rows={1}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            style={{ maxHeight: '120px' }}
            disabled={isLoading || isSaving}
          />
          <button
            onClick={handleSend}
            disabled={(!input.trim() && pendingImages.length === 0) || isLoading || isSaving}
            className={`p-2.5 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ${
              isScenarioMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
